import {
  Stack,
} from "@mui/material";
import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import formatter from "../../tools/formatter.js";
import useEODAAA from "../../deal-builder/hooks/useEODAAA.ts";
import useUsTreasury from "../../deal-builder/hooks/useUsTreasury.ts";
import { FiltersButton } from "./analyzer-page/filters-button.tsx";
import usePricingManagamentAnalyzerStore from "../../stores/pricing-management-analyzer.store.ts";
import { calculateAverages, DealWithStats, filterDeals } from "../utils/deals.utils.ts";
import { addStatsToSavedDeals } from "../../pricing-management/utils/deals.utils.ts";
import usePricingManagementStore from "../../stores/pricing-management.store.ts";
import { round } from "../../tools/utils.js";
import moment from "moment";
import { GenerateReportButton } from "./deals-page/report-button.tsx";
import { DealStatus } from "../../deal-builder/services/deal-builder.ts";

export default function AnalyzePage() {
  const { savedDeals } = usePricingManagementStore();
  const { getAAARateForSavedDeal } = useEODAAA();
  const { getUSTRateForSavedDeal } = useUsTreasury();
  const filters = usePricingManagamentAnalyzerStore();
  const [filteredDeals, setFilteredDeals] = React.useState<DealWithStats[]>([]);
  const [averages, setAverages] = React.useState<ReturnType<typeof calculateAverages>[]>([]);
  React.useEffect(() => { calculateDeals() }, [filters]);
  React.useEffect(() => { calculateDealsAverages() }, [filteredDeals]);

  async function calculateDeals() {
    const dealsWithStats = await addStatsToSavedDeals(
      savedDeals,
      getAAARateForSavedDeal,
      getUSTRateForSavedDeal,
    );

    setFilteredDeals(filterDeals(dealsWithStats, filters));
  }

  async function calculateDealsAverages() {
    setAverages([
      calculateAverages(filteredDeals, [DealStatus.WON, DealStatus.LOST]),
      calculateAverages(filteredDeals, [DealStatus.LOST]),
      calculateAverages(filteredDeals, [DealStatus.WON]),
    ])
  }

  return (
    <Stack direction="column" gap={2} margin={2} sx={{ width: "100%" }}>
      <Paper>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          m={1}
        >
          <FiltersButton />
          <GenerateReportButton />
        </Stack>
      </Paper>

      {averages.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ "& > *": { py: 2 } }}>
                <TableCell>Averages</TableCell>
                <TableCell align="right">
                  <b>TIC Spread</b>
                </TableCell>
                <TableCell align="right">
                  <b>AAA Spread</b>
                </TableCell>
                <TableCell align="right">
                  <b>UST Spread</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ py: 1 }}><b>Lost and Won Bids</b></TableCell>
                <TableCell align="right">{averages[0].avgTicSpread ? formatter.percent(averages[0].avgTicSpread) : "N/A"}</TableCell>
                <TableCell align="right">{averages[0].avgAAASpread ? formatter.percent(averages[0].avgAAASpread) : "N/A"}</TableCell>
                <TableCell align="right">{averages[0].avgUSTSpread ? formatter.percent(averages[0].avgUSTSpread) : "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 1 }}><b>Lost Bids Only</b></TableCell>
                <TableCell align="right">{averages[1].avgTicSpread ? formatter.percent(averages[1].avgTicSpread) : "N/A"}</TableCell>
                <TableCell align="right">{averages[1].avgAAASpread ? formatter.percent(averages[1].avgAAASpread) : "N/A"}</TableCell>
                <TableCell align="right">{averages[1].avgUSTSpread ? formatter.percent(averages[1].avgUSTSpread) : "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 1 }}><b>Won Bids Only</b></TableCell>
                <TableCell align="right">{averages[2].avgTicSpread ? formatter.percent(averages[2].avgTicSpread) : "N/A"}</TableCell>
                <TableCell align="right">{averages[2].avgAAASpread ? formatter.percent(averages[2].avgAAASpread) : "N/A"}</TableCell>
                <TableCell align="right">{averages[2].avgUSTSpread ? formatter.percent(averages[2].avgUSTSpread) : "N/A"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: "16px" }} align="left">
                <b>Bid Date</b>
              </TableCell>
              <TableCell align="left">
                <b>State</b>
              </TableCell>
              <TableCell align="left">
                <b>Issuer</b>
              </TableCell>
              <TableCell align="left">
                <b>Issue</b>
              </TableCell>
              <TableCell align="left">
                <b>Amount</b>
              </TableCell>
              <TableCell align="left">
                <b>Rating Grade</b>
              </TableCell>
              <TableCell align="left">
                <b>Average Life</b>
              </TableCell>
              <TableCell align="left">
                <b>Coupon</b>
              </TableCell>
              <TableCell align="left">
                <b>Cover Bid</b>
              </TableCell>
              <TableCell align="left">
                <b>TIC Spread</b>
              </TableCell>
              <TableCell align="left">
                <b>AAA Spread</b>
              </TableCell>
              <TableCell align="left">
                <b>UST Spread</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDeals.map((dealWithStats, index) => {
              const deal = dealWithStats.deal;

              return (
                <TableRow sx={{ "& > *": { py: 1 } }}  key={index}>
                  <TableCell>
                    {moment(deal.bid_date).format("MM/DD/YYYY")}
                  </TableCell>
                  <TableCell>{deal.state_of_issuance}</TableCell>
                  <TableCell>{deal.borrower}</TableCell>
                  <TableCell>{deal.issuance_title}</TableCell>
                  <TableCell>{formatter.money(deal.par_amount)}</TableCell>
                  <TableCell>{deal.rating_assumption}</TableCell>
                  <TableCell>{round(dealWithStats.deal.average_life, 2)} Y</TableCell>
                  <TableCell>{formatter.percent(deal.bid_rate + (dealWithStats.coverBidSpread || 0))}</TableCell>
                  <TableCell>{dealWithStats.coverBidSpread ? formatter.percent(dealWithStats.coverBidSpread) : "N/A"}</TableCell>
                  <TableCell>{dealWithStats.ticSpread ? formatter.percent(dealWithStats.ticSpread) : "N/A"}</TableCell>
                  <TableCell>{dealWithStats.aaaSpread ? formatter.percent(dealWithStats.aaaSpread) : "N/A"}</TableCell>
                  <TableCell>{dealWithStats.ustSpread ? formatter.percent(dealWithStats.ustSpread) : "N/A"}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
